import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from '@tanstack/react-router'
import { BuildingGroupCreateForm, type BuildingTypeChoice, type BuildingGroupFormData } from './BuildingGroupCreateForm'
import { BuildingCreateForm } from './BuildingCreateForm'
import { WizardStepHeader } from './WizardStepHeader'
import { clampStep, GROUP_WIZARD_STEPS } from './onboardingSteps'
import { useCreateAsset, useAssetTypes, withoutArchivedTypes, useAssetTypeByCode, usePropertyDefinitions, useAsset, useAssetProperties } from '../hooks/useQueries'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '../api/hooks'
import type { MeterTypeCode } from '../lib/meters'

import type { Iso20022Address } from './AddressSearch'
import { wizardAddressProperties } from './wizardAddressProperties'

interface Location {
  lat: number
  lng: number
}

interface BuildingGroupData {
  name: string
  location: Location
  address: string
  iso20022?: Iso20022Address
  placeId?: string | null
  buildingName?: string | null
  buildingType?: BuildingTypeChoice
}

interface BuildingData {
  name: string
}



interface AssetCreateWizardProps {
  resumeAssetUid?: string
  resumeStep?: number
}

export function AssetCreateWizard({ resumeAssetUid, resumeStep }: AssetCreateWizardProps) {
  const { org } = useParams({ from: '/$org/createBuildingGroup' })
  const navigate = useNavigate()
  const { t } = useTranslation('assets')

  // Determine current step: clamp the resume step into this wizard's range (1-2)
  const [currentStep, setCurrentStep] = useState<number>(clampStep(resumeStep, GROUP_WIZARD_STEPS.min, GROUP_WIZARD_STEPS.max))

  // Determine if we're in edit mode (asset_uid is present)
  const isEditMode = !!resumeAssetUid

  // State for different entities
  const [buildingGroupData, setBuildingGroupData] = useState<BuildingGroupData | null>(null)
  const [, setBuildingsData] = useState<BuildingData[]>([])
  const [, setCreatedBuildingAssets] = useState<Array<{ name: string, uid: string }>>([])
  const [createdBuildingGroupAsset, setCreatedBuildingGroupAsset] = useState<{ name: string, uid: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data: allAssetTypesRaw = [] } = useAssetTypes(org || 'demo')
  const assetTypes = useMemo(() => withoutArchivedTypes(allAssetTypesRaw), [allAssetTypesRaw])
  const { data: buildingGroupType } = useAssetTypeByCode(org || 'demo', 'building_group')
  const { data: buildingType } = useAssetTypeByCode(org || 'demo', 'building')
  const { data: outdoorType } = useAssetTypeByCode(org || 'demo', 'outdoor')
  const { data: propertyDefinitions = [] } = usePropertyDefinitions(org || 'demo')
  const createAssetMutation = useCreateAsset(org || 'demo')
  const { request } = useApi()

  // Fetch properties specific to building group type
  const { data: buildingGroupProperties = [] } = useQuery({
    queryKey: ['asset-type-properties', org, buildingGroupType?.uid],
    queryFn: async () => {
      if (!buildingGroupType?.uid) return []
      const response = await request('/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}/properties', 'GET', {
        params: {
          path: {
            orgCode: org || 'demo',
            assetTypeUid: buildingGroupType.uid
          }
        }
      })
      return (response as any).data || []
    },
    enabled: !!buildingGroupType?.uid
  })

  // Fetch existing asset data when in edit mode
  const { data: existingAsset, isLoading: isLoadingAsset } = useAsset(
    org || 'demo',
    isEditMode && resumeAssetUid ? resumeAssetUid : ''
  )


  // Fetch asset properties when we need them to load geolocation data
  const { data: assetProperties = [], isLoading: isLoadingProperties } = useAssetProperties(
    org || 'demo',
    isEditMode && resumeAssetUid ? resumeAssetUid : ''
  )

  // Populate building group data when existing asset is loaded
  useEffect(() => {
    if (existingAsset && isEditMode) {
      // Extract geolocation and address from properties
      const geolocationProp = assetProperties.find(prop =>
        propertyDefinitions.find(def => def.uid === prop.property_uid && def.code === 'geolocation')
      )
      const addressRawProp = assetProperties.find(prop =>
        propertyDefinitions.find(def => def.uid === prop.property_uid && def.code === 'address_raw')
      )

      let location = { lat: 48.8566, lng: 2.3522 } // Default location
      let address = '' // Default address

      // Parse geolocation if available
      if (geolocationProp && geolocationProp.value) {
        try {
          const geoData = JSON.parse(geolocationProp.value)
          if (geoData.lat && geoData.lng) {
            location = { lat: geoData.lat, lng: geoData.lng }
          } else if (geoData.latitude && geoData.longitude) {
            // Backward compatibility for old format
            location = { lat: geoData.latitude, lng: geoData.longitude }
          }
        } catch (error) {
          console.warn('Failed to parse geolocation property:', error)
        }
      }

      // Extract address if available
      if (addressRawProp && addressRawProp.value) {
        address = addressRawProp.value
      }

      setBuildingGroupData({
        name: existingAsset.name,
        location,
        address
      })
      setCreatedBuildingGroupAsset({
        name: existingAsset.name,
        uid: existingAsset.uid
      })
    }
  }, [existingAsset, isEditMode, assetProperties, propertyDefinitions])


  // Update frontend_data with last_wizard_step
  const updateWizardStep = async (assetUid: string, step: number) => {
    try {
      await request('/api/v1/organizations/{orgCode}/assets/{assetUid}', 'PATCH', {
        params: { path: { orgCode: org || 'demo', assetUid } },
        body: { frontend_data: { incomplete: true, last_wizard_step: step } }
      })
    } catch (err) {
      console.error('Failed to update wizard step:', err)
    }
  }

  // Function to set properties on an asset
  const setPropertiesOnAsset = async (assetUid: string, data: BuildingGroupData) => {
    try {
      // Prefer properties from the building group type, but fallback to global properties
      const availableProperties = [...buildingGroupProperties, ...propertyDefinitions]

      const findProp = (code: string) => availableProperties.find(p => p.code === code)

      const propertiesToSet: Array<{ name: string; uid: string; value: any }> = []

      // address_raw is the typed text; address_iso20022 only comes from a picked
      // suggestion (see wizardAddressProperties, spec 2026-09-30-09).
      const addr = wizardAddressProperties(data.address, data.iso20022)
      const addressRawProp = findProp('address_raw')
      if (addressRawProp && addr.address_raw) {
        propertiesToSet.push({ name: 'address_raw', uid: addressRawProp.uid, value: addr.address_raw })
      }
      const addressIso20022Prop = findProp('address_iso20022')
      if (addressIso20022Prop && addr.address_iso20022) {
        propertiesToSet.push({ name: 'address_iso20022', uid: addressIso20022Prop.uid, value: addr.address_iso20022 })
      }

      // geolocation
      const geolocationProp = findProp('geolocation')
      if (geolocationProp) {
        propertiesToSet.push({ name: 'geolocation', uid: geolocationProp.uid, value: { lat: data.location.lat, lng: data.location.lng } })
      }

      // google_place_id — if available from AddressSearch
      if (data.placeId) {
        const placeIdProp = findProp('google_place_id')
        if (placeIdProp) {
          propertiesToSet.push({ name: 'google_place_id', uid: placeIdProp.uid, value: data.placeId })
        }
      }

      // Building type (selected on step 1). The former optional fields (cadastral
      // reference, common name, client internal code) are no longer collected in the
      // wizard — they are edited later from the asset detail page.
      if (data.buildingType) {
        const prop = findProp('building_type')
        if (prop) propertiesToSet.push({ name: 'building_type', uid: prop.uid, value: data.buildingType })
      }

      for (const property of propertiesToSet) {
        try {
          await request('/api/v1/organizations/{orgCode}/assets/{assetUid}/properties/{propertyUid}', 'PUT', {
            params: { path: { orgCode: org || 'demo', assetUid, propertyUid: property.uid } },
            body: { value: property.value }
          })
        } catch (err) {
          console.error(`Failed to set ${property.name} property:`, err)
        }
      }
    } catch (error) {
      console.error('Error setting properties on asset:', error)
    }
  }

  const handleBuildingGroupNext = async (data: BuildingGroupFormData) => {
    // The site name is no longer captured — derive it from the address (prefer the
    // Google place name, fall back to the raw address).
    const derivedName = (data.buildingName?.trim() || data.address.trim()) || t('create_building.fallback_building_group')
    const groupData: BuildingGroupData = { ...data, name: derivedName }
    setBuildingGroupData(groupData)
    setError(null)

    try {
      if (!isEditMode) {
        // Find building group asset type (codes are lowercase in the database)
        const buildingGroupType = assetTypes.find(type =>
          type.code === 'building_group' || type.code === 'BUILDING_GROUP'
        )

        if (!buildingGroupType && assetTypes.length === 0) {
          setError(t('create_building.error.no_asset_types'))
          return
        }

        // Create building group asset with frontend_data tracking
        const buildingGroupAsset = await createAssetMutation.mutateAsync({
          name: derivedName,
          asset_type_uid: buildingGroupType?.uid || assetTypes[0]?.uid,
          frontend_data: {
            incomplete: true,
            completionPercentage: 10
          }
        })

        // Store the created building group asset
        setCreatedBuildingGroupAsset({ name: derivedName, uid: buildingGroupAsset.uid })

        // Set properties for address and geolocation - wait a bit for property definitions to be loaded
        if (propertyDefinitions.length === 0) {
          console.warn('Property definitions not loaded yet, waiting...')
          // Small delay to allow property definitions to load
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
        await setPropertiesOnAsset(buildingGroupAsset.uid, groupData)
        await updateWizardStep(buildingGroupAsset.uid, 1)

        // Update URL with asset_uid and advance to step 2 (create buildings)
        navigate({
          to: '/$org/createBuildingGroup',
          params: { org: org || 'demo' },
          search: { asset_uid: buildingGroupAsset.uid, step: 2 }
        })
      } else {
        // In edit mode, just update the data and proceed to step 2
        navigate({
          to: '/$org/createBuildingGroup',
          params: { org: org || 'demo' },
          search: { asset_uid: resumeAssetUid, step: 2 }
        })
      }

      setCurrentStep(2)
    } catch (err: any) {
      console.error('Error creating building group asset:', err)
      const fields = err?.fields || err?.body?.fields
      if (fields && Array.isArray(fields)) {
        setError(fields.map((f: any) => f.message).join(', '))
      } else {
        setError(err?.message || err?.title || 'An error occurred while creating the asset.')
      }
    }
  }

  const handleBuildingsNext = async (buildings: BuildingData[], createOutdoor: boolean, meterTypeCodes: MeterTypeCode[] = []) => {
    console.log('=== HANDLE BUILDINGS NEXT START ===')
    console.log('Buildings to create:', buildings)
    console.log('isEditMode:', isEditMode)
    console.log('resumeAssetUid:', resumeAssetUid)
    console.log('createdBuildingGroupAsset:', createdBuildingGroupAsset)

    setBuildingsData(buildings)

    try {
      let parentAssetUid = isEditMode ? resumeAssetUid : createdBuildingGroupAsset?.uid
      console.log('Determined parentAssetUid:', parentAssetUid)

      if (!parentAssetUid) {
        console.warn('Parent asset UID not found, creating building group first')

        // If we don't have a parent asset UID, we need to create the building group first
        // This can happen when navigating directly to step 3
        const buildingGroupAsset = await createAssetMutation.mutateAsync({
          name: buildingGroupData?.name || t('create_building.fallback_building_group'),
          asset_type_uid: buildingGroupType?.uid || assetTypes[0]?.uid,
          frontend_data: {
            incomplete: true,
            completionPercentage: 10
          }
        })

        parentAssetUid = buildingGroupAsset.uid
        setCreatedBuildingGroupAsset({ name: buildingGroupData?.name || 'Building Group', uid: buildingGroupAsset.uid })
        console.log('Created building group asset:', buildingGroupAsset.uid)
      }

      console.log('Creating buildings:', buildings.map(b => b.name))
      console.log('Parent asset UID:', parentAssetUid)
      console.log('About to check isEditMode:', isEditMode)

      // In step 3, we always create buildings regardless of edit mode
      // Edit mode just means we have an existing building group, but we still need to create buildings

      // Create buildings with the correct building type
      const createdBuildings: Array<{ name: string, uid: string }> = []

      try {
        for (const building of buildings) {
          const typeToUse = buildingType?.uid || assetTypes[0]?.uid

          const buildingAsset = await createAssetMutation.mutateAsync({
            name: building.name,
            asset_type_uid: typeToUse,
            parent_asset_uid: parentAssetUid,
            frontend_data: {
              incomplete: true,
              completionPercentage: 10
            }
          })

          console.log(`Created building asset: ${building.name} with UID: ${buildingAsset.uid}`)
          createdBuildings.push({ name: building.name, uid: buildingAsset.uid })
        }

        console.log('All buildings created successfully:', createdBuildings)

        // Outdoor entity (spec 2026-09-13-05): proposed by the step 2
        // checkbox, checked by default. Submitting the step is the
        // confirmation — unticking it creates nothing (§0, no path requires
        // the entity). Created after the buildings so it never blocks them.
        if (createOutdoor && outdoorType?.uid && parentAssetUid) {
          try {
            await createAssetMutation.mutateAsync({
              name: t('create_building.buildings.outdoor_default_name'),
              asset_type_uid: outdoorType.uid,
              parent_asset_uid: parentAssetUid
            })
          } catch (outdoorError) {
            console.error('Error creating outdoor asset:', outdoorError)
            // Non-blocking: the site is still usable without it (§0).
          }
        }

        // General meters (spec 2026-10-02-06): one of each checked type on
        // each building, through the idempotent provisioning endpoint.
        // Non-blocking like the outdoor entity: a building without its meters
        // is still a building, and the equipment tab can add them later.
        if (meterTypeCodes.length > 0) {
          for (const building of createdBuildings) {
            try {
              await request('/api/v1/organizations/{orgCode}/assets/{assetUid}/meters/provision', 'POST', {
                params: { path: { orgCode: org || 'demo', assetUid: building.uid } },
                body: { meter_type_codes: meterTypeCodes, targets: 'self' }
              })
            } catch (meterError) {
              console.error('Error creating building meters:', meterError)
            }
          }
        }

        // Store the created building assets for floor creation
        setCreatedBuildingAssets(createdBuildings)
        if (parentAssetUid) await updateWizardStep(parentAssetUid, 2)

        // Navigate to createBuilding page for building configuration
        navigate({
          to: '/$org/createBuilding',
          params: { org: org || 'demo' },
          search: { building_group_uid: parentAssetUid }
        })

      } catch (buildingError) {
        console.error('Error creating individual building:', buildingError)
        // Even if individual buildings fail, still navigate to createBuilding to prevent hanging
        setError('Some buildings could not be created. Please try again.')
        navigate({
          to: '/$org/createBuilding',
          params: { org: org || 'demo' },
          search: { building_group_uid: parentAssetUid }
        })
      }
    } catch (error) {
      console.error('Error in handleBuildingsNext:', error)
      // Prevent hanging by always navigating, even on error
      setError('An error occurred during building creation. Please try again.')
      const fallbackAssetUid = isEditMode ? resumeAssetUid : createdBuildingGroupAsset?.uid || 'unknown'
      navigate({
        to: '/$org/createBuilding',
        params: { org: org || 'demo' },
        search: { building_group_uid: fallbackAssetUid }
      })
    }
  }


  const handleCancel = () => {
    navigate({
      to: '/$org/assets',
      params: { org: org || 'demo' },
      search: { showBuildingGroups: true, showBuildings: true, q: '', asset_type_uid: undefined } as any
    })
  }

  const handleBack = () => {
    const newStep = currentStep - 1
    if (newStep >= 1) {
      const searchParams = isEditMode ? { asset_uid: resumeAssetUid, step: newStep } : { step: newStep }
      navigate({
        to: '/$org/createBuildingGroup',
        params: { org: org || 'demo' },
        search: searchParams
      })
      setCurrentStep(newStep)
    }
  }

  // Show loading state while fetching existing asset or properties
  if (isEditMode && (isLoadingAsset || isLoadingProperties)) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-lg p-6">
              <WizardStepHeader step={currentStep} title={t('create_building.loading_asset')} />
              <p className="text-muted-foreground">
                {t('create_building.loading_asset_msg')}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4">
        {error && (
          <div className="max-w-4xl mx-auto mb-4 p-4 rounded-lg border border-destructive/50 bg-destructive/10 text-destructive text-sm" data-testid="wizard-error">
            {error}
          </div>
        )}
        {currentStep === 1 && (
          <BuildingGroupCreateForm
            org={org || 'demo'}
            initialData={buildingGroupData || undefined}
            excludeAssetUid={resumeAssetUid}
            onNext={handleBuildingGroupNext}
            onCancel={handleCancel}
          />
        )}

        {currentStep === 2 && (
          <BuildingCreateForm
            buildingGroupName={buildingGroupData?.name || t('create_building.fallback_building_group')}
            onNext={handleBuildingsNext}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        )}

      </div>
    </div>
  )
}